import { getBaseUrl } from '@/utils/utils'

let cachedKey: { authToken: string; apiKey: string } | undefined
let pending: { authToken: string; promise: Promise<string> } | undefined

export const loadEsriApiKey = (authToken: string): Promise<string> => {
    if (!authToken) return Promise.reject(new Error('Authentication is required to load the ESRI API key.'))
    if (cachedKey?.authToken === authToken) return Promise.resolve(cachedKey.apiKey)
    if (pending?.authToken === authToken) return pending.promise

    cachedKey = undefined
    const basePath = import.meta.env.VITE_API_HOST || `${getBaseUrl()}/api`
    const promise = fetch(`${basePath}/vault/esri.json`, {
        cache: 'no-store',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'X-Client-Id': 'X2B Static Web',
        },
    })
        .then(async response => {
            if (!response.ok) {
                throw new Error(`Unable to load map credentials (HTTP ${response.status}).`)
            }
            if (response.redirected) {
                throw new Error('The map credentials request was redirected. Please sign in again.')
            }
            const contentType = response.headers.get('Content-Type') || ''
            if (contentType.toLowerCase().startsWith('text/plain')) return response.text()
            try {
                return await response.json() as unknown
            } catch {
                throw new Error('The map credentials endpoint did not return valid JSON.')
            }
        })
        .then((response: unknown) => {
            const value = typeof response === 'string'
                ? response
                : response && typeof response === 'object'
                    ? (response as { ESRI_TOKEN?: unknown }).ESRI_TOKEN
                        ?? (response as { apiKey?: unknown }).apiKey
                        ?? (response as { esriApiKey?: unknown }).esriApiKey
                    : undefined
            if (typeof value !== 'string' || !value.trim()) {
                throw new Error('The vault returned an invalid ESRI API key.')
            }
            const apiKey = value.trim()
            if (pending?.promise === promise) cachedKey = { authToken, apiKey }
            return apiKey
        })
        .finally(() => {
            if (pending?.promise === promise) pending = undefined
        })
    pending = { authToken, promise }
    return promise
}

export const getEsriApiKey = (): string => {
    if (!cachedKey) throw new Error('The ESRI API key has not been loaded yet.')
    return cachedKey.apiKey
}
